import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  useGetMarketStatusQuery,
  usePatchProgramPricingMutation,
  useSyncMarketRatesMutation,
  type MarketPricing,
} from '@/apis/extended/admin/interestRateApi';

const PROGRAM_ORDER = [
  'conventional',
  'fha',
  'va',
  'usda',
  'dscr',
  'commercial',
  'fixAndFlip',
  'bridge',
  'heloc',
  'heloan',
  'secondMortgage',
];

const fmtRate = (v: number | null | undefined) =>
  typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(3)}%` : '—';

const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(v)
    ? new Date(`${v}T12:00:00`)
    : new Date(v);
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
};

type Draft = {
  mode: MarketPricing['mode'];
  indexSeries: string;
  margin: string;
};

/**
 * Daily market pricing: each program's base rate = latest market index +
 * margin, refreshed automatically from FRED. Admins pick the index, set the
 * margin, or switch a program to manual (base rate stays exactly as typed).
 */
export const MarketRatesPanel = () => {
  const { data: status, isLoading, error } = useGetMarketStatusQuery();
  const [syncMarket, { isLoading: syncing }] = useSyncMarketRatesMutation();
  const [patchPricing, { isLoading: saving }] =
    usePatchProgramPricingMutation();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [message, setMessage] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);

  useEffect(() => {
    if (!status) return;
    const next: Record<string, Draft> = {};
    Object.entries(status.programs).forEach(([key, p]) => {
      next[key] = {
        mode: p.pricing?.mode || 'manual',
        indexSeries: p.pricing?.indexSeries || '',
        margin: String(p.pricing?.margin ?? 0),
      };
    });
    setDrafts(next);
  }, [status]);

  const handleSync = async () => {
    setMessage(null);
    try {
      const result = await syncMarket().unwrap();
      setMessage({
        severity: 'success',
        text: result.changes.length
          ? `Repriced ${
              result.changes.length
            } program(s) from market data as of ${fmtDate(result.marketAsOf)}.`
          : `Rates already current (market data as of ${fmtDate(
              result.marketAsOf
            )}).`,
      });
    } catch (e: any) {
      setMessage({
        severity: 'error',
        text: e?.data?.message || 'Market sync failed',
      });
    }
  };

  const handleSave = async (program: string) => {
    const d = drafts[program];
    if (!d) return;
    const margin = Number(d.margin);
    if (!Number.isFinite(margin)) {
      setMessage({ severity: 'error', text: 'Margin must be a number' });
      return;
    }
    setMessage(null);
    try {
      await patchPricing({
        program,
        mode: d.mode,
        ...(d.indexSeries ? { indexSeries: d.indexSeries } : {}),
        margin,
        reason: 'Admin pricing change',
      }).unwrap();
      setMessage({
        severity: 'success',
        text: `${status?.programs[program]?.label || program} pricing saved.`,
      });
    } catch (e: any) {
      setMessage({
        severity: 'error',
        text: e?.data?.message || 'Could not save pricing',
      });
    }
  };

  if (isLoading) return null;
  if (error || !status) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        Daily market pricing status is unavailable.
      </Alert>
    );
  }

  const indexValue = (seriesId: string) =>
    status.indices.find((i) => i.seriesId === seriesId)?.value ?? null;
  const sched = status.scheduler;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Daily market pricing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Base rate = market index + margin, refreshed automatically every{' '}
              {sched.intervalHours} hours. Market data as of{' '}
              <strong>{fmtDate(status.marketAsOf)}</strong>
              {status.termSpreads?.fifteenYear != null &&
                ` · 15-yr vs 30-yr spread ${status.termSpreads.fifteenYear.toFixed(
                  3
                )}%`}
            </Typography>
            <Typography
              variant="caption"
              color={sched.lastError ? 'error' : 'text.secondary'}
            >
              {sched.lastError
                ? `Last sync failed: ${sched.lastError}`
                : sched.lastSuccessAt
                ? `Last sync ${new Date(sched.lastSuccessAt).toLocaleString()}`
                : 'No sync yet since the server started'}
              {!sched.enabled && ' · automatic sync disabled'}
            </Typography>
          </Box>
          <Button variant="contained" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
        </Stack>

        {message && (
          <Alert
            severity={message.severity}
            sx={{ mb: 2 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          {status.indices.map((i) => (
            <Chip
              key={i.seriesId}
              variant="outlined"
              label={`${i.label}: ${fmtRate(i.value)}${
                i.date ? ` (${fmtDate(i.date)})` : ''
              }`}
            />
          ))}
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Program</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Index</TableCell>
                <TableCell align="right">Index value</TableCell>
                <TableCell align="right">Margin</TableCell>
                <TableCell align="right">Base rate today</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {PROGRAM_ORDER.filter((k) => status.programs[k]).map((key) => {
                const p = status.programs[key];
                const d = drafts[key];
                if (!d) return null;
                const isMarket = d.mode === 'market';
                const idx = d.indexSeries ? indexValue(d.indexSeries) : null;
                const preview =
                  isMarket && idx != null && Number.isFinite(Number(d.margin))
                    ? idx + Number(d.margin)
                    : null;
                const dirty =
                  d.mode !== (p.pricing?.mode || 'manual') ||
                  d.indexSeries !== (p.pricing?.indexSeries || '') ||
                  Number(d.margin) !== (p.pricing?.margin ?? 0);
                const update = (patch: Partial<Draft>) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], ...patch },
                  }));
                return (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {p.label || key}
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={d.mode}
                        onChange={(e) =>
                          update({ mode: e.target.value as Draft['mode'] })
                        }
                      >
                        <MenuItem value="market">Market</MenuItem>
                        <MenuItem value="manual">Manual</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={d.indexSeries}
                        disabled={!isMarket}
                        onChange={(e) =>
                          update({ indexSeries: String(e.target.value) })
                        }
                        sx={{ minWidth: 200 }}
                      >
                        {status.indices.map((i) => (
                          <MenuItem key={i.seriesId} value={i.seriesId}>
                            {i.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="right">
                      {isMarket ? fmtRate(idx) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={d.margin}
                        disabled={!isMarket}
                        onChange={(e) => update({ margin: e.target.value })}
                        inputProps={{
                          step: 0.125,
                          style: { textAlign: 'right', width: 70 },
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        {fmtRate(
                          dirty && preview != null ? preview : p.baseRate
                        )}
                      </strong>
                      {dirty && preview != null && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          was {fmtRate(p.baseRate)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={!dirty || saving}
                        onClick={() => handleSave(key)}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1 }}
        >
          Editing a program&apos;s base rate below while it&apos;s in Market
          mode sets its margin instead, so it keeps following the market. The
          rate grids move with the base rate: each cell prices as its difference
          from the base rate it was saved against.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MarketRatesPanel;
